import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const FAQContainer = styled.section`
  padding: 120px 5% 80px;
  background: #000000;
  
  @media (max-width: 768px) {
    padding: 80px 20px 60px;
  }
`;

const FAQContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(28px, 3.5vw, 40px);
  font-weight: 400;
  color: white;
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const SectionSubtitle = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.6;
`;

const FAQList = styled.div`
  width: 100%;
`;

const FAQItem = styled.div``;

const QuestionButton = styled.button`
  width: 100%;
  padding: 28px 0;
  background: none;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  cursor: pointer;
  text-align: left;
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 768px) {
    padding: 24px 0;
    gap: 16px;
  }
`;

const QuestionText = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: white;
  line-height: 1.5;
  transition: color 0.3s ease;
  
  ${QuestionButton}:hover & {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const IconWrapper = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.isOpen ? '#FF2300' : 'rgba(255, 35, 0, 0.4)'};
  flex-shrink: 0;
  transition: background 0.3s ease;
`;

const AnswerWrapper = styled(motion.div)`
  overflow: hidden;
`;

const AnswerContent = styled.div`
  padding-bottom: 28px;
  
  @media (max-width: 768px) {
    padding-bottom: 24px;
  }
`;

const AnswerText = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
  margin: 0;
  max-width: 90%;
`;

const ContactSection = styled.div`
  width: 100%;
  margin-top: 60px;
  padding-top: 60px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const ContactTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: white;
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

const ContactDescription = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
  margin: 0;
`;

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData = [
    {
      question: "What is DIZEE?",
      answer: "DIZEE is an AI workspace for music business management. It connects to your platforms to provide analytics, automation, and insights."
    },
    {
      question: "How do I get started?",
      answer: "Sign up, connect your platforms, and DIZEE will begin analyzing your data. Setup takes less than 10 minutes."
    },
    {
      question: "Which platforms does DIZEE connect to?",
      answer: "DIZEE connects to major streaming platforms including Spotify, Apple Music, and YouTube Music. We also integrate with social media, ticketing, and financial tools."
    },
    {
      question: "How does pricing work?",
      answer: "DIZEE uses credits where different actions consume different amounts. Viewing dashboards uses minimal credits, while AI insights use more. Most users find included credits sufficient."
    },
    {
      question: "Is my data secure?",
      answer: "Yes. We use enterprise-grade security including encryption and regular audits. Your data is never shared without consent."
    },
    {
      question: "Can I manage multiple artists?",
      answer: "Yes. Pro and Elite plans include team features and multi-artist dashboards with role-based permissions."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes. Cancel anytime with no fees. Your account stays active until the end of your billing period."
    }
  ];

  return (
    <FAQContainer>
      <FAQContent>
        <SectionHeader>
          <SectionTitle>FAQ</SectionTitle>
        </SectionHeader>

        <FAQList>
          {faqData.map((item, index) => (
            <FAQItem key={index}>
              <QuestionButton onClick={() => toggleQuestion(index)}>
                <QuestionText>{item.question}</QuestionText>
                <IconWrapper isOpen={openIndex === index} />
              </QuestionButton>
              
              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <AnswerWrapper
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ 
                      height: 'auto', 
                      opacity: 1,
                      transition: {
                        height: {
                          duration: 0.4,
                          ease: [0.4, 0, 0.2, 1]
                        },
                        opacity: {
                          duration: 0.3,
                          ease: 'easeOut',
                          delay: 0.1
                        }
                      }
                    }}
                    exit={{ 
                      height: 0, 
                      opacity: 0,
                      transition: {
                        height: {
                          duration: 0.4,
                          ease: [0.4, 0, 0.2, 1]
                        },
                        opacity: {
                          duration: 0.2,
                          ease: 'easeIn'
                        }
                      }
                    }}
                  >
                    <AnswerContent>
                      <AnswerText>{item.answer}</AnswerText>
                    </AnswerContent>
                  </AnswerWrapper>
                )}
              </AnimatePresence>
            </FAQItem>
          ))}
        </FAQList>

        <ContactSection>
          <ContactTitle>Need help?</ContactTitle>
          <ContactDescription>Contact support for assistance</ContactDescription>
        </ContactSection>
      </FAQContent>
    </FAQContainer>
  );
};

export default FAQ;
